import { useState } from "react";
import { Sparkles, ShieldCheck, ArrowLeft, Lock, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link, useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

function formatApiError(detail) {
  if (detail == null) return "Bir şeyler ters gitti, tekrar deneyin.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("niptuck.tr@gmail.com");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const loginWithPassword = async (e) => {
    e?.preventDefault?.();
    if (!email || !password) return toast.error("Email ve şifre gerekli");
    setBusy(true);
    try {
      const { data } = await apiClient.post(
        "/auth/login-password",
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      toast.success("Giriş başarılı");
      navigate("/admin", { replace: true, state: { user: data } });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative overflow-hidden" data-testid="login-page">
      <div className="absolute inset-0 pointer-events-none opacity-50"
        style={{
          background:
            "radial-gradient(800px 400px at 30% 0%, rgba(179,155,122,0.18), transparent 60%), radial-gradient(700px 500px at 90% 100%, rgba(139,155,136,0.14), transparent 60%)",
        }}
      />
      <div className="relative max-w-md w-full mx-6">
        <Link to="/" className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground inline-flex items-center gap-2 mb-8 font-display">
          <ArrowLeft className="w-3 h-3" /> Ana sayfa
        </Link>
        <div className="bg-white rounded-3xl border border-border/40 p-10 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #B39B7A, #8B9B88)" }}>
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-display text-xs uppercase tracking-widest text-muted-foreground">Dr. Hüseyin Tırman</div>
              <div className="font-serif text-xl">Yönetici Paneli</div>
            </div>
          </div>

          {/* Telegram one-tap login hint */}
          <div className="mt-7 p-4 rounded-2xl border" style={{ background: "#F0F7FB", borderColor: "#CFE4EF" }} data-testid="telegram-hint">
            <div className="flex items-start gap-2">
              <Send className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
              <div className="text-xs leading-relaxed">
                <div className="font-semibold text-foreground mb-1">En kolay giriş: Telegram'dan tek tıkla</div>
                <div className="text-muted-foreground">Her sabah 10:00'da Telegram'a gelen günlük raporda "🔐 Yönetim Paneli (tek tık)" linkine bastığınızda şifresiz giriş yapabilirsiniz.</div>
              </div>
            </div>
          </div>

          {/* Password login (backup) */}
          <form onSubmit={loginWithPassword} className="mt-6 space-y-4" data-testid="password-login-form">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-display">veya şifre ile giriş</div>
            <div>
              <Label className="text-xs" htmlFor="login-email">Email</Label>
              <Input
                id="login-email"
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="niptuck.tr@gmail.com"
                autoComplete="username"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-xs" htmlFor="login-password">Şifre</Label>
              <Input
                id="login-password"
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                className="mt-1.5"
              />
            </div>
            <Button
              type="submit"
              data-testid="password-login-btn"
              disabled={busy}
              className="w-full h-12 rounded-full font-display"
              style={{ background: "#2C3531" }}
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
              Şifre ile giriş yap
            </Button>
          </form>

          <div className="flex items-start gap-2 mt-6 text-xs text-muted-foreground bg-secondary/40 rounded-xl p-3">
            <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5 text-primary" />
            <p>Sadece yetkilendirilmiş yönetici hesapları panele erişebilir. 5 hatalı denemede 15 dakikalık otomatik kilit uygulanır.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
